import { Component, Prop, Mixins } from "vue-property-decorator";
import { mapState } from "vuex";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import BrandsStore from "@/mixins/brandsStore";

// Enums/Data
import { ProductTypes } from "@/data/enums/products";
import { DataModules } from "@/store/modules/data/index";

// Types
import { IProduct } from "@/models/products";
import { IContract, IContractProduct } from "@/models/contracts";
import { IInvoice } from "@/models/invoices";
import { IState } from "@/store";
import { IBrand } from "@/models/brands";
import { UUID } from "@/data/enums";

@Component({
  computed: {
    ...mapState({
      brandSettingsScope(state: IState, getters) {
        return getters[`data/${DataModules.BRANDS}/brandScope`](
          this.contractBrandId
        );
      },
      brandSettingsData(state: IState, getters) {
        return (
          getters[`data/single`]({
            storeModule: DataModules.BRANDS,
            scope: this.brandSettingsScope
          }) || {}
        );
      }
    })
  }
})
export default class ModifyProductMixin extends Mixins(
  BrandsStore,
  RequestPromises
) {
  public brandSettingsData!: IBrand;
  public brandSettingsScope!: string;

  // Props
  @Prop({ required: true })
  public readonly contract!: IContract;

  @Prop({ required: true })
  public readonly oldContractProduct!: IContractProduct;

  @Prop({ default: "$changeProduct" })
  public readonly scopeChangeProduct!: string;

  // Data
  public isProcessing = false;

  //Getters
  public get products(): IProduct[] {
    return this.$store.getters["data/listArray"]({
      storeModule: DataModules.CATALOGUE,
      scope: this.scopeChangeProduct
    });
  }

  public getProducts(ignoreStored = false) {
    return this.$store.dispatch(`data/${DataModules.CATALOGUE}/list`, {
      clientApi: true,
      ignoreStored,
      isBasketContext: true,
      params: this.params,
      scope: this.scopeChangeProduct,
      vm: this
    });
  }

  public async getBrandSettings(ignoreStored = false) {
    if (this.brandId !== UUID.ORG) return Promise.resolve();

    return this.$store.dispatch(`data/${DataModules.BRANDS}/getBrandSettings`, {
      id: this.contractBrandId,
      scope: this.brandSettingsScope,
      vm: this,
      ignoreStored
    });
  }

  public get params() {
    return {
      currency_id: this.contract.currency_id,
      brand_id: this.contractBrandId,
      account_id: this.contract.account_id,
      "filter[billing_cycle_months|neq]": 0,
      "filter[product_type]": [
        ProductTypes.SINGLE_PRODUCT,
        ProductTypes.PRODUCT_BUNDLE
      ].join(),
      order: "order",
      limit: 0,
      with: "image"
    };
  }

  public get contractBrandId() {
    return this.contract.brand_id;
  }

  public get taxType() {
    return this.brandId !== UUID.ORG
      ? this.$_.get(this.$store, "state.brand.tax_type", 1)
      : this.$_.get(this.brandSettingsData, "tax_type", 1);
  }

  public changeProduct(data): Promise<IInvoice> {
    return this.$store.dispatch(
      `data/${DataModules.CONTRACTS_PRODUCTS}/changeProduct`,
      {
        contractId: this.contract.id,
        contractProductId: this.oldContractProduct.id,
        data
      }
    );
  }

  // Lifecycle methods
  public beforeDestroy() {
    this.$store.dispatch("data/bin", {
      storeModule: DataModules.CATALOGUE,
      scope: this.scopeChangeProduct,
      vm: this
    });

    if (this.brandId === UUID.ORG) {
      this.$store.dispatch("data/binSingle", {
        storeModule: DataModules.BRANDS,
        scope: this.brandSettingsScope,
        vm: this
      });
    }
  }
}
